import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, Play } from "lucide-react";
import { api, normalizeBeats } from "@/lib/api";
import { scrollToId } from "@/lib/scroll";

const lines = ["EVERY ARTIST", "HAS A SOUND.", "LET DJ MIAMI", "FIND IT."];

const Hero = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const yLogo = useTransform(scrollYProgress, [0, 1], [0, 130]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const rz = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 90, damping: 16 });

  const { data: beats = [] } = useQuery({
    queryKey: ["beats"],
    queryFn: async () => normalizeBeats((await api.get("/beats")).data),
  });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onMouseLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section id="top" ref={heroRef} className="relative min-h-[100svh] overflow-hidden flex items-center">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1690398388394-6a57f7f4fff1?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt=""
          className="w-full h-full object-cover opacity-[0.55]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050507]/70 via-[#050507]/55 to-[#050507]" />
        <div className="absolute -top-32 -left-32 w-[560px] h-[560px] rounded-full bg-[#D65BB4] opacity-20 blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-[560px] h-[560px] rounded-full bg-[#4DBDBE] opacity-[0.14] blur-[140px]" />
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 w-full grid grid-cols-1 lg:grid-cols-2 gap-16 items-center pt-32 pb-24">
        <motion.div style={{ y: yText }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-[#4DBDBE]" />
            <span className="text-xs tracking-[0.4em] text-white/60 uppercase font-semibold">
              Music Producer • DJ • Miami, FL
            </span>
            <span className="h-px w-10 bg-[#4DBDBE]" />
          </motion.div>

          <h1 className="font-display font-black uppercase leading-[0.95] tracking-tight text-[clamp(2.1rem,8vw,4.2rem)]">
            {lines.map((l, i) => (
              <div key={l} className="overflow-hidden">
                <motion.div
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.15 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                  className={i === 3 ? "text-vice" : ""}
                >
                  {l}
                </motion.div>
              </div>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-6 text-base md:text-lg text-white/70 leading-relaxed max-w-lg"
          >
            Multi-platinum-credited producer and DJ out of Miami. I cultivate sounds that are
            unique and personal to the artist — preview any beat for 60 seconds, lease it, or
            own your next hit.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-beats-btn"
              onClick={() => scrollToId("vault")}
              className="group flex items-center gap-3 bg-[#D65BB4] px-8 py-4 text-sm uppercase tracking-widest font-bold hover:shadow-[0_0_40px_rgba(214,91,180,0.5)] transition-shadow"
            >
              <Play size={16} className="group-hover:scale-110 transition-transform" /> Find Your Sound
            </button>
            <button
              data-testid="hero-cta-how-btn"
              onClick={() => scrollToId("producer")}
              className="border border-white/25 px-8 py-4 text-sm uppercase tracking-widest hover:border-[#4DBDBE] hover:text-[#4DBDBE] transition-colors"
            >
              How it works
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex gap-10"
          >
            {[
              { v: `${beats.length || 20}`, l: "Beats in the vault" },
              { v: "60s", l: "Free previews" },
              { v: "4", l: "License tiers" },
            ].map((s) => (
              <div key={s.l} className="border-l-2 border-[#D65BB4] pl-4">
                <div className="font-display text-2xl md:text-3xl font-bold" data-testid={`hero-stat-${s.l.toLowerCase().replace(/\s+/g, "-")}`}>{s.v}</div>
                <div className="text-xs uppercase tracking-widest text-white/50 mt-1">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yLogo }} className="relative hidden lg:block" onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
          <div
            className="relative"
            style={{ perspective: "1200px" }}
          >
            <motion.div
              style={{ rotateX: rx, rotateY: ry, rotateZ: rz, transformStyle: "preserve-3d" }}
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative w-full max-w-[520px] mx-auto"
            >
              <img
                src="/images/logo-vice.png"
                alt="DJ Miami Vice logo"
                className="relative w-full"
                style={{ transform: "translateZ(60px)" }}
                data-testid="hero-logo-img"
              />
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="absolute bottom-10 right-0 glass px-6 py-4"
          >
            <div className="text-xs uppercase tracking-[0.3em] text-[#4DBDBE]">Producer • DJ</div>
            <div className="text-xs uppercase tracking-[0.3em] text-white/50 mt-1">Engineer</div>
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-btn"
        onClick={() => scrollToId("vault")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 hover:text-[#D65BB4] transition-colors"
        aria-label="Scroll to the vault"
      >
        <ArrowDown size={22} className="animate-bounce" />
      </motion.button>
    </section>
  );
};

export default Hero;
